import { Injectable, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription, firstValueFrom, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, take } from 'rxjs/operators';

import { SignalRService } from './singal-r.service';
import { LookupService } from './api/lookup.service';
import { selectToken } from '../../store/selectors/token.selectors';

@Injectable({ providedIn: 'root' })
export class AppInitializationService implements OnDestroy {
  readonly initializing$ = new BehaviorSubject<boolean>(false);

  private _initialized = false;
  private _initPromise: Promise<void> | null = null;
  private _currentToken = '';
  private _tokenSub: Subscription | null = null;

  private readonly store = inject(Store);
  private readonly signalRService = inject(SignalRService);
  private readonly lookupService = inject(LookupService);

  get initialized(): boolean {
    return this._initialized;
  }

  ensureInitialized(): Promise<void> {
    if (this._initialized) return Promise.resolve();
    if (this._initPromise) return this._initPromise;

    this._initPromise = this._runInitialization();
    return this._initPromise;
  }

  reset(): void {
    this._initialized = false;
    this._initPromise = null;
    this._tokenSub?.unsubscribe();
    this._tokenSub = null;
    this.signalRService.stopConnection();
    this.initializing$.next(false);
  }

  ngOnDestroy(): void {
    this._tokenSub?.unsubscribe();
  }

  private async _runInitialization(): Promise<void> {
    try {
      this._subscribeToToken();

      const token = await firstValueFrom(this.store.select(selectToken).pipe(take(1)));
      if (token) {
        this._currentToken = token;
        await Promise.all([
          this._startSignalR(),
          this._prefetchCurrencies(),
        ]);
      }

      this._initialized = true;
    } catch (err) {
      console.error('[AppInit] Initialization failed:', err);
      this._initialized = true;
    } finally {
      this.initializing$.next(false);
    }
  }

  private _subscribeToToken(): void {
    if (this._tokenSub) return;

    this._tokenSub = this.store
      .select(selectToken)
      .pipe(
        filter((t): t is string => !!t),
        distinctUntilChanged(),
      )
      .subscribe((token) => {
        this._currentToken = token;
      });
  }

  private async _startSignalR(): Promise<void> {
    try {
      await this.signalRService.startConnection(() => this._currentToken);
    } catch (err) {
      console.error('[AppInit] SignalR failed:', err);
    }
  }

  /** Warm LMS currency lookup cache (facility clone, etc.). */
  private _prefetchCurrencies(): Promise<void> {
    return firstValueFrom(
      this.lookupService.getCurrencies().pipe(catchError(() => of([]))),
    ).then(() => {});
  }
}
